import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { of } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ExternalProposal } from '../models/property-proposals.model';

@Injectable({
    providedIn: 'root'
})
export class ProposalsService {

    constructor(private http: HttpClient, private cookieService: CookieService) { }

    createProposal(data: any) {
        return this.http.post<any>(`${environment.apiUrl}/proposals`, data, { withCredentials: true })
            .pipe(first());
    }

    createExternalProposal(data: any) {
        return this.http.post<any>(`${environment.apiUrl}/proposals/external`, data, { withCredentials: true })
            .pipe(first());
    }

    acceptProposal(proposalId: any, val: any) {
        return this.http.patch<any>(`${environment.apiUrl}/proposals/accept/${proposalId}`, { value: val }, { withCredentials: true })
            .pipe(first());
    }

    acceptProposalWorkflow(proposalId: any, data: any) {
        const body = typeof data === 'object' ? data : { value: data };
        return this.http.patch<any>(`${environment.apiUrl}/proposals/workflow/accept/${proposalId}`, body, { withCredentials: true })
            .pipe(first());
    }

    /** `obs`: motivo da recusa (opcional), guardado no histórico. */
    declineProposal(proposalId: any, val: any, obs?: string) {
        return this.http.patch<any>(`${environment.apiUrl}/proposals/decline/${proposalId}`, { value: val, obs: obs || undefined }, { withCredentials: true })
            .pipe(first());
    }

    declineProposalWorkflow(proposalId: any, data: any) {
        const body = typeof data === 'object' ? data : { value: data };
        return this.http.patch<any>(`${environment.apiUrl}/proposals/workflow/decline/${proposalId}`, body, { withCredentials: true })
            .pipe(first());
    }

    counterProposal(proposalId: any, data: any) {
        return this.http.patch<any>(`${environment.apiUrl}/proposals/counter/${proposalId}`, data, { withCredentials: true })
            .pipe(first());
    }

    deleteProposal(id: any) {
        return this.http.delete<any>(`${environment.apiUrl}/proposals/${id}`, { withCredentials: true })
            .pipe(first());
    }

    /** "Enviar para o promotor": Para / Assunto / Corpo pré-preenchidos pelo servidor. */
    getPromoterEmailPreview(proposalId: any) {
        return this.http.get<{ to: string[]; subject: string; body: string; replyTo: string; canReserve?: boolean }>(
            `${environment.apiUrl}/proposals/promoter-email/${proposalId}`, { withCredentials: true })
            .pipe(first());
    }

    /**
     * Devolve a entrada "Em Análise" do histórico (null num reenvio) e, com
     * `reserveProperty`, o id do estado comercial Reservado aplicado ao imóvel.
     */
    sendPromoterEmail(proposalId: any, data: { to: string[]; subject: string; body: string; reserveProperty?: boolean }) {
        return this.http.post<{ success: boolean; history: any | null; reservedId?: number | null }>(`${environment.apiUrl}/proposals/promoter-email/${proposalId}`, data, { withCredentials: true })
            .pipe(first());
    }

    getStatus() {
        // Sem cookie o ngx-cookie-service devolve string vazia e o URL fica
        // /proposals/status/, que cai na rota /proposals/:id no back-end.
        const lang = this.cookieService.get('lang') || 'pt';
        return this.http.get<any>(`${environment.apiUrl}/proposals/status/${lang}`, { withCredentials: true }).pipe(first())
    }

    getFilters() {
        const lang = this.cookieService.get('lang') || 'pt';
        return this.http.get<any>(`${environment.apiUrl}/proposals/filters/${lang}`, { withCredentials: true }).pipe(first())
    }

    getPendingProposals(page: any, search: any, filters: any, pageSize?: number) {
        let params = new HttpParams().set('page', page)
            .set('filters', filters ? JSON.stringify(filters) : JSON.stringify({}));
        if (search && search !== '') params = params.set('search', search);
        if (pageSize !== undefined && pageSize !== null) params = params.set('pageSize', pageSize);
        return this.http.get<any>(`${environment.apiUrl}/proposals/pending`, { params, withCredentials: true }).pipe(first())
    }

    exportProposalsExcel(type: 'pending' | 'history', search: any, filters: any) {
        let params = new HttpParams().set('type', type)
            .set('filters', filters ? JSON.stringify(filters) : JSON.stringify({}));
        if (search && search !== '') params = params.set('search', search);
        return this.http.get(`${environment.apiUrl}/proposals/export/excel`, { params, responseType: 'blob', withCredentials: true }).pipe(first())
    }

    getProposalsHistory(page: any, search: any, filters: any) {
        let params = new HttpParams().set('page', page)
            .set('filters', filters ? JSON.stringify(filters) : JSON.stringify({}));
        if (search && search !== '') params = params.set('search', search);
        return this.http.get<any>(`${environment.apiUrl}/proposals/history`, { params, withCredentials: true }).pipe(first())
    }

    getProposalsSent(page: any, search: any, filters: any) {
        let params = new HttpParams().set('page', page)
            .set('filters', filters ? JSON.stringify(filters) : JSON.stringify({}));
        if (search && search !== '') params = params.set('search', search);
        return this.http.get<any>(`${environment.apiUrl}/proposals/sent`, { params, withCredentials: true }).pipe(first())
    }

    getProposalsReceived(page: any, search: any, filters: any) {
        let params = new HttpParams().set('page', page)
            .set('filters', filters ? JSON.stringify(filters) : JSON.stringify({}));
        if (search && search !== '') params = params.set('search', search);
        return this.http.get<any>(`${environment.apiUrl}/proposals/received`, { params, withCredentials: true }).pipe(first())
    }

    checkProposal(proposalId: any, data: any) {
        return this.http.patch<any>(`${environment.apiUrl}/proposals/check/${proposalId}`, data, { withCredentials: true })
            .pipe(first());
    }

    advanceProposalWorkflowStep(proposalId: any, data: any) {
        return this.http.patch<any>(`${environment.apiUrl}/proposals/workflow/step/${proposalId}`, data, { withCredentials: true })
            .pipe(first());
    }

    uploadProposalWorkflowDocument(historyId: any, data: any) {
        return this.http.patch<any>(`${environment.apiUrl}/proposals/workflow/document/${historyId}`, data, { withCredentials: true })
            .pipe(first());
    }

    getExternalProposals(page: any, search: any, filters: any, pageSize?: number) {
        let params = new HttpParams().set('page', page)
            .set('filters', filters ? JSON.stringify(filters) : JSON.stringify({}));
        if (search && search !== '') params = params.set('search', search);
        if (pageSize !== undefined && pageSize !== null) params = params.set('pageSize', pageSize);
        return this.http.get<any>(`${environment.apiUrl}/proposals/external`, { params, withCredentials: true }).pipe(first())
    }

    acceptExternalProposal(proposalId: any, val: any) {
        return this.http.patch<any>(`${environment.apiUrl}/proposals/external/accept/${proposalId}`, { value: val }, { withCredentials: true })
            .pipe(first());
    }

    declineExternalProposal(proposalId: any, val: any, obs?: string) {
        return this.http.patch<any>(`${environment.apiUrl}/proposals/external/decline/${proposalId}`, { value: val, obs: obs || undefined }, { withCredentials: true })
            .pipe(first());
    }

    counterExternalProposal(proposalId: any, data: any) {
        return this.http.patch<any>(`${environment.apiUrl}/proposals/external/counter/${proposalId}`, data, { withCredentials: true })
            .pipe(first());
    }

    deleteExternalProposal(id: any) {
        return this.http.delete<any>(`${environment.apiUrl}/proposals/external/${id}`, { withCredentials: true })
            .pipe(first());
    }

    checkExternalProposal(proposalId: any, data: any) {
        return this.http.patch<any>(`${environment.apiUrl}/proposals/external/check/${proposalId}`, data, { withCredentials: true })
            .pipe(first());
    }

    findExternalProposalById(id: any) {
        return this.http.get<ExternalProposal>(`${environment.apiUrl}/proposals/external/${id}`, { withCredentials: true }).pipe(first())
    }

    getProposalById(id: any) {
        return this.http.get<any>(`${environment.apiUrl}/proposals/${id}`, { withCredentials: true }).pipe(first())
    }

    updateProposal(data: any, id: any) {
        return this.http.patch<any>(`${environment.apiUrl}/proposals/${id}`, data, { withCredentials: true })
            .pipe(first());
    }
}
